import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";
import { SearchBox } from "../../../../../components/Search";
import loader from "../../../../../components/recharts/loader.gif";
import ToggleTab from "./ChartTableToggle";
import { ModTable } from "./ModTable";
import ModChart from "./ModChart";
import ModHeader from "./ModHeader";
import Modal from "./Modal";
import { ReactComponent as Edit } from "../../../../../icons/edit_solid.svg";
import {
  CreateBidTable,
  fetchModTableChartData,
  ModUpdateData,
} from "../../../../../Rest_api/DemoRestApi";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
import "./ModIndex.css";
import Tooltip from "../../../../../components/tooltip";
import BlockRangeSelector from "../../../../../components/BlockRangeSelector/BlockRangeSelector";
import { useMeritEngineNavigation } from "../../useMeritEngineNavigation";
import MainHeader from "../../DateSession";

const DataTabs = ["chart", "table"];

interface GeneratorData {
  ownership: any;
  generator_type: any;
  date: string;
  generator: string;
  margin_aggressive_sell: number;
  margin_buy: number;
  margin_conservative_sell: number;
  must_run: number;
  time_slot__slot: number;
  total_capacity: number;
  vc: number;
}

export default function ModChartTable() {
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );
  const { navigateToPage } = useMeritEngineNavigation();
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(DataTabs[1]);
  const [data, setData] = useState<GeneratorData[]>([]);
  const [modData, setModData] = useState<GeneratorData[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalFilter, setModalFilter] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [uniqueGeneratorType, setUniqueGeneratorType] = useState<any[]>([]);
  const [selectedGeneratorType, setSelectedGeneratorType] = useState<any[]>([]);
  const [uniqueOwnership, setUniqueOwnership] = useState<any[]>([]);
  const [selectedOwnership, setSelectedOwnership] = useState<any[]>([]);
  const [uniqueGeneratorName, setUniqueGeneratorName] = useState<any[]>([]);
  const [selectedGeneratorName, setSelectedGeneratorName] = useState<any[]>([]);

  const GotoBidTable = async () => {
    try {
      setSubmitLoading(true);
      await CreateBidTable({
        date: preSelectedDate,
        session: modSelectedSession,
      });
      navigateToPage("bid");
    } catch (error) {
      setError("Failed to create bid table");
      console.error("Error creating bid table:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const downloadTable = () => {
    const table = document.getElementById("my-table");
    if (!table) {
      setError("Table is not visible right now!");
      return;
    }
    const worksheet = XLSX.utils.table_to_sheet(table);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "Mod_table.xlsx");
  };

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;

    try {
      setLoading(true);
      const res = await fetchModTableChartData(
        preSelectedDate,
        modSelectedSession
      );
      setData(res.data);
      setUniqueBlock(
        Array.from(
          new Set(
            res.data.map(
              (item: { time_slot__slot: any }) => item.time_slot__slot
            )
          )
        )
      );

      setUniqueGeneratorType(
        Array.from(
          new Set(
            res.data.map((item: { generator_type: any }) => item.generator_type)
          )
        )
      );

      setUniqueOwnership(
        Array.from(
          new Set(res.data.map((item: { ownership: any }) => item.ownership))
        )
      );
      setUniqueGeneratorName(
        Array.from(
          new Set(res.data.map((item: { generator: any }) => item.generator))
        )
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [modSelectedSession, preSelectedDate]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  useEffect(() => {
    let filtered: GeneratorData[] = data;

    if (selectedGeneratorType.length > 0) {
      const types = selectedGeneratorType.map((t: any) => t.value ?? t);
      filtered = filtered.filter((row) => types.includes(row.generator_type));
    }

    if (selectedOwnership.length > 0) {
      const owners = selectedOwnership.map((o: any) => o.value ?? o);
      filtered = filtered.filter((row) => owners.includes(row.ownership));
    }
    if (selectedGeneratorName.length > 0) {
      const generator = selectedGeneratorName.map((o: any) => o.value ?? o);
      filtered = filtered.filter((row) => generator.includes(row.generator));
    }

    if (selectedBlock.length > 0) {
      const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      filtered = filtered.filter((row) =>
        blocks.includes(Number(row.time_slot__slot))
      );
    }

    setModData(filtered);
  }, [
    data,
    selectedGeneratorType,
    selectedOwnership,
    selectedBlock,
    selectedGeneratorName,
  ]);

  async function handleUpdateReview(data: any) {
    try {
      setSubmitLoading(true);
      await ModUpdateData({
        date: preSelectedDate,
        session: modSelectedSession,
        data: data,
      });
      handleFetchData();
      setModalOpen(false);
      setModalFilter(false);
      (window as any).resetNextTabs?.("MOD");
    } catch (error) {
      setError("Failed to update MOD data");
      console.error("Error updating MOD data:", error);
    } finally {
      setSubmitLoading(false);
    }
  }

  return (
    <div className="page-wrapper">
      <div className="flex flex-col w-full">
        <MainHeader />

        <div className="tabContent">
          <div className="h-[calc(-73px+100vh)] p-4">
            <div className="w-full h-full flex flex-col gap-4">
              <ModHeader title="Merit Order Dispatch" />
              <div className="gas-chart-container !gap-1">
                <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] pb-2">
                  <div className="flex items-center gap-3">
                    <ToggleTab
                      tabs={DataTabs}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                    />
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Select Slot"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                    <SearchBox
                      options={uniqueGeneratorType.map((item) => ({
                        value: item,
                        label: item,
                      }))}
                      isMany={true}
                      placeholder="Generator Type"
                      selectedOptions={selectedGeneratorType}
                      onChange={setSelectedGeneratorType}
                    />
                    <SearchBox
                      options={uniqueOwnership.map((item) => ({
                        value: item,
                        label: item,
                      }))}
                      isMany={true}
                      placeholder="Generator Ownership"
                      selectedOptions={selectedOwnership}
                      onChange={setSelectedOwnership}
                    />
                    <SearchBox
                      options={uniqueGeneratorName.map((item) => ({
                        value: item,
                        label: item,
                      }))}
                      isMany={true}
                      placeholder="Generator Name"
                      selectedOptions={selectedGeneratorName}
                      onChange={setSelectedGeneratorName}
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setModalOpen(true)}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium"
                    >
                      <Edit className="w-5 h-5" />
                      Modify
                    </button>
                    <button
                      onClick={downloadTable}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C]"
                    >
                      <span>Download Data</span>
                      <Download className="w-5 h-5" />
                    </button>
                    <button
                      disabled={submitLoading}
                      onClick={GotoBidTable}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
                    >
                      {submitLoading
                        ? "Processing..."
                        : "Finalize & Create Bid"}
                    </button>
                  </div>
                </div>
                <div className="chartTable">
                  {loading ? (
                    <div className="h-full w-full bg-white flex items-center justify-center">
                      <img
                        src={loader}
                        width={200}
                        alt="loader"
                        className="block mt-[5rem]"
                      />
                    </div>
                  ) : (
                    <>
                      {activeTab === "table" && (
                        <ModTable
                          setModalOpen={setModalOpen}
                          data={modData}
                          setModalFilter={setModalFilter}
                          handleUpdateReview={handleUpdateReview}
                          modalFilter={modalFilter}
                          uniqueGeneratorType={uniqueGeneratorType}
                          uniqueOwnership={uniqueOwnership}
                          uniqueBlock={uniqueBlock}
                          loading={submitLoading}
                        />
                      )}
                      {activeTab === "chart" && (
                        <ModChart
                          selectedGeneratorType={selectedGeneratorType}
                          selectedOwnership={selectedOwnership}
                          selectedGenerator={selectedGeneratorName}
                          selectedBlock={selectedBlock}
                          data={data}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
              <ErrorPopup error={error} onClose={() => setError(null)} />
              <div className="gna-footer-container">
                <div className="grid grid-cols-3 justify-between gap-5 ">
                  <Tooltip
                    note={
                      <p>Variable Charges are auto-fetched from the system.</p>
                    }
                    text={` 1.  DC: Declared Capacity / Entitlement (MW).
                            2. VC: Variable Charge (Rs/kWh).
                            3. MR: Must Run / Technical Minima (%).
                            4. BM: Buy Margin (Rs/kWh).
                            5. SM: Sell Margin Conservative (Rs/kWh).
                            6. SAM: Sell Margin Aggressive (Rs/kWh).`}
                  >
                    <p className="gna-footer-text">Source</p>
                  </Tooltip>
                  <p className="gna-footer-text col-span-1 text-center">
                    Powered by <strong>GNA Energy</strong>
                  </p>
                  <strong className="gna-footer-text col-span-1 text-end">
                    Beta Version
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
          <div className="w-full">
            <div className="h-[calc(-73px+100vh)]">
              <div className="w-full h-full flex flex-col gap-4">
                <div
                  className="gas-chart-container"
                  style={{
                    height: "calc(-60px + 100vh)",
                    borderRadius: "0px!important",
                  }}
                >
                  <div className="gas-heading gap-x-10">
                    <span className="text-[#F1935C] text-lg font-medium">
                      Modification Mode
                    </span>
                    <h2 className="chartHeading !text-lg !font-medium w-100">
                      MOD Table
                    </h2>
                    <div className="flex gap-x-6 justify-end">
                      <button
                        onClick={() => setModalFilter(true)}
                        className="flex items-center gap-2 px-4 py-3 text-sm rounded-xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium"
                      >
                        <Edit className="w-5 h-5" />
                        Batch Customize
                      </button>
                    </div>
                  </div>
                  <ModTable
                    setModalFilter={setModalFilter}
                    handleUpdateReview={handleUpdateReview}
                    modalFilter={modalFilter}
                    uniqueGeneratorType={uniqueGeneratorType}
                    uniqueOwnership={uniqueOwnership}
                    uniqueBlock={uniqueBlock}
                    loading={submitLoading}
                    setModalOpen={setModalOpen}
                    mode={"edit"}
                    handleSubmit={handleUpdateReview}
                    maxHeight="calc(-60px + 100vh)"
                    data={modData}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
